import {Graphics, Rectangle} from "pixi.js";
import {inches} from "../../const";
import {glyphStyle, GlyphFootprintContext, GlyphRect, hasCategory, HardwareCategoryFields, HardwareGlyph, hitAreaOf} from "./HardwareGlyph";

// a protection plate (mop or armour) seen face-on: a plain rectangle centred on the hub,
// as wide as the leaf less a small inset at each edge. Heights are typical catalogue
// values (scene units = mm). A kick plate is cosmetic, not placed (see ../DoorKickPlate).
export const KICK_PLATE_HEIGHT = 10 * inches;
export const MOP_PLATE_HEIGHT = 6 * inches;
export const ARMOUR_PLATE_HEIGHT = 36 * inches;
export const PLATE_EDGE_INSET = 1 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

// edge-view thickness off the door face, drawn heavier than a real plate so it shows.
const PLATE_THICKNESS = 0.25 * inches;

export function isProtectionPlate(item: HardwareCategoryFields): boolean {
    return hasCategory(item, "Mop Plate", "Armour Plate");
}

// the plate height for an item's category; a kick plate when the category is unknown.
export function plateHeight(item: HardwareCategoryFields): number {
    if(hasCategory(item, "Armour Plate")) return ARMOUR_PLATE_HEIGHT;
    if(hasCategory(item, "Mop Plate")) return MOP_PLATE_HEIGHT;
    return KICK_PLATE_HEIGHT;
}

// the plate centred on the hub, as wide as the leaf less the edge insets.
function footprint(ctx: GlyphFootprintContext): GlyphRect[] {
    const cx = ctx.hub.x;
    const cy = ctx.hub.y;
    const width = Math.max(0, (ctx.rect.x1 - ctx.rect.x0) - 2 * PLATE_EDGE_INSET);
    const height = plateHeight(ctx.item);
    return [{x0: cx - width / 2, y0: cy - height / 2, x1: cx + width / 2, y1: cy + height / 2}];
}

export const protectionPlate: HardwareGlyph = {
    id: "protectionPlate",
    targets: ["door"],
    matches: isProtectionPlate,
    draw(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const [plate] = footprint(ctx);

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.2);
        g.drawRect(plate.x0, plate.y0, plate.x1 - plate.x0, plate.y1 - plate.y0);
        g.endFill();

        return hitAreaOf([plate], HIT_PAD);
    },
    footprint,
    // seen from the latch edge: a thin plate lying on the face.
    drawEdge(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const fx = ctx.face.x;
        const cy = ctx.face.y;
        const height = plateHeight(ctx.item);
        const x0 = Math.min(fx, fx + ctx.out * PLATE_THICKNESS);

        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawRect(x0, cy - height / 2, PLATE_THICKNESS, height);
        g.endFill();

        return new Rectangle(x0 - HIT_PAD, cy - height / 2 - HIT_PAD, PLATE_THICKNESS + 2 * HIT_PAD, height + 2 * HIT_PAD);
    },
};
